import { useState, useEffect, type FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { PerfexSyncModal } from '../components/PerfexSyncModal';
import { useT } from '../i18n';
import { SendWindowsEditor, validateWindows } from '../components/SendWindowsEditor';

interface Settings {
  id: string;
  openaiApiKey?: string;
  groqApiKey?: string;
  anthropicApiKey?: string;
  chatwootUrl?: string;
  chatwootAccountId?: string;
  chatwootApiToken?: string;
  perfexUrl?: string;
  perfexToken?: string;
  sendMinIntervalSeconds?: number;
  sendDailyLimitPerNumber?: number;
  sendWindowStart?: string;
  sendWindowEnd?: string;
  sendTimezone?: string;
  requireConsent?: boolean;
  autoPauseFailureRate?: number;
  optOutKeywords?: string;
  optOutConfirmation?: string | null;
}

interface SendPolicyForm {
  sendMinIntervalSeconds: string;
  sendDailyLimitPerNumber: string;
  sendWindowStart: string;
  sendWindowEnd: string;
  sendTimezone: string;
  requireConsent: boolean;
  autoPauseFailureRate: string;
  optOutKeywords: string;
  optOutConfirmation: string;
}

const DEFAULT_SEND_POLICY: SendPolicyForm = {
  sendMinIntervalSeconds: '30',
  sendDailyLimitPerNumber: '200',
  sendWindowStart: '09:00',
  sendWindowEnd: '21:00',
  sendTimezone: 'Asia/Dubai',
  requireConsent: false,
  autoPauseFailureRate: '10',
  optOutKeywords: 'STOP,PARAR,SAIR,CANCELAR,UNSUBSCRIBE,إلغاء,الغاء,إيقاف,ايقاف,توقف',
  optOutConfirmation: '',
};

const settingsSchema = z.object({
  openaiApiKey: z.string().optional(),
  groqApiKey: z.string().optional(),
  anthropicApiKey: z.string().optional(),
  chatwootUrl: z.string().optional(),
  chatwootAccountId: z.string().optional(),
  chatwootApiToken: z.string().optional(),
  perfexUrl: z.string().optional(),
  perfexToken: z.string().optional(),
});

type SettingsFormData = z.infer<typeof settingsSchema>;

export function SettingsPage() {
  const t = useT();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState<'openai' | 'groq' | 'claude' | 'chatwoot' | 'perfex' | null>(null);
  const [sendPolicy, setSendPolicy] = useState<SendPolicyForm>(DEFAULT_SEND_POLICY);
  // Cadastro self-service (apenas o dono da plataforma): abrir inscrições e escolher o número que envia o código
  const [signup, setSignup] = useState({ signupEnabled: false, otpSessionName: '', signupTrialDays: 14 });
  const [savingSignup, setSavingSignup] = useState(false);
  // O dono da plataforma decide quais integrações as empresas veem nesta tela
  const [visibility, setVisibility] = useState({ showAiIntegrations: false, showChatwootIntegration: false, showPerfexIntegration: false });
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [connections, setConnections] = useState<Array<{ name: string; displayName?: string; status: string }>>([]);
  const [savingSendPolicy, setSavingSendPolicy] = useState(false);
  // Horários de envio: decisão da própria empresa, ao contrário dos limites de ritmo
  const [windows, setWindows] = useState<Array<{ start: string; end: string }>>([]);
  const [savingWindows, setSavingWindows] = useState(false);
  const { user } = useAuth();
  // Dono da plataforma: só ele abre as inscrições e escolhe o número que envia o código
  const isPlatformOwner = user?.role === 'SUPERADMIN';
  // A política de envio é vista por todos, mas só o dono da plataforma pode alterá-la
  const canManageSendPolicy = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';
  const canEditSendPolicy = isPlatformOwner;

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };


  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
    onErrors: (errors) => {
      console.log('🔴 Erros de validação:', errors);
    }
  });

  useEffect(() => {
    loadSettings();

    // Listen for tenant changes from header selector
    const handleTenantChange = () => {
      loadSettings();
    };

    window.addEventListener('superadmin-tenant-changed', handleTenantChange);
    return () => {
      window.removeEventListener('superadmin-tenant-changed', handleTenantChange);
    };
  }, [user]);

  const loadSettings = async () => {
    try {
      let url = '/api/settings';

      if (user?.role === 'SUPERADMIN') {
        const selectedTenantId = localStorage.getItem('superadmin_selected_tenant');
        if (selectedTenantId) {
          url = `/api/settings?tenantId=${selectedTenantId}`;
        }
      }

      const response = await authenticatedFetch(url);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setValue('openaiApiKey', data.openaiApiKey || '');
        setValue('groqApiKey', data.groqApiKey || '');
        setValue('anthropicApiKey', data.anthropicApiKey || '');
        setValue('chatwootUrl', data.chatwootUrl || '');
        setValue('chatwootAccountId', data.chatwootAccountId || '');
        setValue('chatwootApiToken', data.chatwootApiToken || '');
        setValue('perfexUrl', data.perfexUrl || '');
        setValue('perfexToken', data.perfexToken || '');
        setSendPolicy({
          sendMinIntervalSeconds: String(data.sendMinIntervalSeconds ?? DEFAULT_SEND_POLICY.sendMinIntervalSeconds),
          sendDailyLimitPerNumber: String(data.sendDailyLimitPerNumber ?? DEFAULT_SEND_POLICY.sendDailyLimitPerNumber),
          sendWindowStart: data.sendWindowStart || DEFAULT_SEND_POLICY.sendWindowStart,
          sendWindowEnd: data.sendWindowEnd || DEFAULT_SEND_POLICY.sendWindowEnd,
          sendTimezone: data.sendTimezone || DEFAULT_SEND_POLICY.sendTimezone,
          requireConsent: !!data.requireConsent,
          autoPauseFailureRate: String(data.autoPauseFailureRate ?? DEFAULT_SEND_POLICY.autoPauseFailureRate),
          optOutKeywords: data.optOutKeywords ?? DEFAULT_SEND_POLICY.optOutKeywords,
          optOutConfirmation: data.optOutConfirmation || '',
        });
        setVisibility({
          showAiIntegrations: !!data.showAiIntegrations,
          showChatwootIntegration: !!data.showChatwootIntegration,
          showPerfexIntegration: !!data.showPerfexIntegration,
        });
        setWindows(Array.isArray(data.sendWindows) && data.sendWindows.length > 0
          ? data.sendWindows.map((w: any) => ({ start: w.start, end: w.end }))
          : [{ start: data.sendWindowStart || '09:00', end: data.sendWindowEnd || '21:00' }]);
        setSignup({
          signupEnabled: !!data.signupEnabled,
          otpSessionName: data.otpSessionName || '',
          signupTrialDays: Number(data.signupTrialDays) || 14,
        });
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error(t('set.errLoad'));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: SettingsFormData) => {
    try {
      let requestData = data;

      if (user?.role === 'SUPERADMIN') {
        const selectedTenantId = localStorage.getItem('superadmin_selected_tenant');
        if (selectedTenantId) {
          requestData = { ...data, tenantId: selectedTenantId };
        }
      }

      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const responseData = await response.json();
        toast.success(t('set.okIntegration'));
        setActiveModal(null);
        await loadSettings();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || t('set.errIntegration'));
      }
    } catch (error) {
      console.error('Erro ao salvar configurações de integração:', error);
      toast.error(t('set.errIntegration'));
    }
  };

  useEffect(() => {
    if (!isPlatformOwner) return;
    authenticatedFetch('/api/waha/sessions')
      .then(response => (response.ok ? response.json() : []))
      .then(data => setConnections(Array.isArray(data) ? data : []))
      .catch(() => setConnections([]));
  }, [isPlatformOwner]);

  const saveVisibility = async (next: typeof visibility) => {
    setVisibility(next);
    setSavingVisibility(true);
    try {
      const response = await authenticatedFetch('/api/settings', { method: 'PUT', body: JSON.stringify(next) });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      toast.success(t('set.visibilitySaved'));
    } catch {
      toast.error(t('set.errSave'));
    } finally {
      setSavingVisibility(false);
    }
  };

  const saveSignupSettings = async (e: FormEvent) => {
    e.preventDefault();
    if (signup.signupEnabled && !signup.otpSessionName) {
      toast.error(t('set.signupErrConnection'));
      return;
    }

    setSavingSignup(true);
    try {
      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify({
          signupEnabled: signup.signupEnabled,
          otpSessionName: signup.otpSessionName || null,
          signupTrialDays: Number(signup.signupTrialDays) || 14,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      toast.success(t('set.signupSaved'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save');
    } finally {
      setSavingSignup(false);
    }
  };

  const saveWindows = async (e: FormEvent) => {
    e.preventDefault();

    const problem = validateWindows(windows);
    if (problem) {
      toast.error(problem === 'SAME_TIME' ? t('win.errSame') : t('set.errWindow'));
      return;
    }

    setSavingWindows(true);
    try {
      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify({
          sendWindows: windows,
          sendTimezone: sendPolicy.sendTimezone.trim() || DEFAULT_SEND_POLICY.sendTimezone,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      toast.success(t('win.saved'));
      await loadSettings();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('win.errSave'));
    } finally {
      setSavingWindows(false);
    }
  };

  const saveSendPolicy = async (e: FormEvent) => {
    e.preventDefault();

    const minInterval = Number(sendPolicy.sendMinIntervalSeconds);
    const dailyLimit = Number(sendPolicy.sendDailyLimitPerNumber);
    const failureRate = Number(sendPolicy.autoPauseFailureRate);

    if (!Number.isInteger(minInterval) || minInterval < 5 || minInterval > 3600) {
      toast.error(t('set.errInterval'));
      return;
    }
    if (!Number.isInteger(dailyLimit) || dailyLimit < 1 || dailyLimit > 10000) {
      toast.error(t('set.errDaily'));
      return;
    }
    if (!Number.isInteger(failureRate) || failureRate < 5 || failureRate > 100) {
      toast.error(t('set.errFailure'));
      return;
    }
    if (!sendPolicy.sendWindowStart || !sendPolicy.sendWindowEnd) {
      toast.error(t('set.errWindow'));
      return;
    }

    setSavingSendPolicy(true);
    try {
      const requestData: Record<string, unknown> = {
        sendMinIntervalSeconds: minInterval,
        sendDailyLimitPerNumber: dailyLimit,
        sendWindowStart: sendPolicy.sendWindowStart,
        sendWindowEnd: sendPolicy.sendWindowEnd,
        sendTimezone: sendPolicy.sendTimezone.trim() || DEFAULT_SEND_POLICY.sendTimezone,
        requireConsent: sendPolicy.requireConsent,
        autoPauseFailureRate: failureRate,
        optOutKeywords: sendPolicy.optOutKeywords.trim(),
        optOutConfirmation: sendPolicy.optOutConfirmation.trim() || null,
      };

      if (user?.role === 'SUPERADMIN') {
        const selectedTenantId = localStorage.getItem('superadmin_selected_tenant');
        if (selectedTenantId) {
          requestData.tenantId = selectedTenantId;
        }
      }

      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        toast.success(t('set.policySaved'));
        await loadSettings();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.error || t('set.policyErrSave'));
      }
    } catch (error) {
      console.error('Erro ao salvar política de envio:', error);
      toast.error(t('set.policyErrSave'));
    } finally {
      setSavingSendPolicy(false);
    }
  };

  const removeIntegration = async (type: 'openai' | 'groq' | 'claude' | 'chatwoot' | 'perfex') => {
    const integrationNames = {
      openai: 'OpenAI',
      groq: 'Groq',
      claude: 'Claude',
      chatwoot: 'Chatwoot',
      perfex: 'Perfex CRM'
    };

    if (!confirm(`Are you sure you want to remove the ${integrationNames[type]} integration?`)) {
      return;
    }

    try {
      let requestData: any = {};

      if (type === 'openai') {
        requestData.openaiApiKey = '';
      } else if (type === 'groq') {
        requestData.groqApiKey = '';
      } else if (type === 'claude') {
        requestData.anthropicApiKey = '';
      } else if (type === 'chatwoot') {
        requestData.chatwootUrl = '';
        requestData.chatwootAccountId = '';
        requestData.chatwootApiToken = '';
      } else if (type === 'perfex') {
        requestData.perfexUrl = '';
        requestData.perfexToken = '';
      }

      if (user?.role === 'SUPERADMIN') {
        const selectedTenantId = localStorage.getItem('superadmin_selected_tenant');
        if (selectedTenantId) {
          requestData.tenantId = selectedTenantId;
        }
      }

      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        toast.success(`${integrationNames[type]} integration removed successfully`);
        setActiveModal(null);
        await loadSettings();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || t('set.errRemoveIntegration'));
      }
    } catch (error) {
      console.error('Erro ao remover integração:', error);
      toast.error(t('set.errRemoveIntegration'));
    }
  };


  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('set.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header
        title={t('set.title')}
        subtitle={t('set.subtitle')}
      />

      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          {(isPlatformOwner || visibility.showAiIntegrations) && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-6 text-gray-900">
              🔗 {t('set.aiTitle')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('set.aiHelp')}
            </p>


            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* OpenAI Button */}
              <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <span className="text-green-600 font-semibold">🤖</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">OpenAI</h3>
                      <p className="text-sm text-gray-500">{t('set.openaiDesc')}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      settings?.openaiApiKey
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {settings?.openaiApiKey ? t('set.configured') : t('set.notConfigured')}
                    </span>
                    <button
                      onClick={() => setActiveModal('openai')}
                      className="px-3 py-1 bg-gray-800 text-white text-sm rounded hover:bg-gray-900"
                    >{t('set.configure')}</button>
                  </div>
                </div>
              </div>

              {/* Groq Button */}
              <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <span className="text-orange-600 font-semibold">⚡</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Groq</h3>
                      <p className="text-sm text-gray-500">{t('set.groqDesc')}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      settings?.groqApiKey
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {settings?.groqApiKey ? t('set.configured') : t('set.notConfigured')}
                    </span>
                    <button
                      onClick={() => setActiveModal('groq')}
                      className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700"
                    >{t('set.configure')}</button>
                  </div>
                </div>
              </div>

              {/* Claude Button */}
              <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <span className="text-amber-700 font-semibold">✳</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Claude</h3>
                      <p className="text-sm text-gray-500">{t('set.claudeDesc')}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      settings?.anthropicApiKey
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {settings?.anthropicApiKey ? t('set.configured') : t('set.notConfigured')}
                    </span>
                    <button
                      onClick={() => setActiveModal('claude')}
                      className="px-3 py-1 bg-amber-700 text-white text-sm rounded hover:bg-amber-800"
                    >{t('set.configure')}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          )}

          {/* Integração Chatwoot */}
          {(isPlatformOwner || visibility.showChatwootIntegration) && (
          <div className="bg-white rounded-lg shadow p-6 mt-6">
            <h2 className="text-lg font-semibold mb-6 text-gray-900">
              💬 {t('set.chatwootTitle')}
            </h2>
            <p className="text-gray-600 mb-6">{t('set.chatwootHelp')}</p>

            <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-blue-600 font-semibold">💬</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Chatwoot</h3>
                    <p className="text-sm text-gray-500">{t('set.chatwootDesc')}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    settings?.chatwootUrl && settings?.chatwootAccountId && settings?.chatwootApiToken
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {settings?.chatwootUrl && settings?.chatwootAccountId && settings?.chatwootApiToken ? t('set.configured') : t('set.notConfigured')}
                  </span>
                  <button
                    onClick={() => setActiveModal('chatwoot')}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >{t('set.configure')}</button>
                </div>
              </div>
            </div>
          </div>

          )}

          {/* Integração Perfex CRM */}
          {(isPlatformOwner || visibility.showPerfexIntegration) && (
          <div className="bg-white rounded-lg shadow p-6 mt-6">
            <h2 className="text-lg font-semibold mb-6 text-gray-900">
              🔧 {t('set.perfexTitle')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('set.perfexHelp')}
            </p>

            <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <span className="text-purple-600 font-semibold">🔧</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Perfex CRM</h3>
                    <p className="text-sm text-gray-500">{t('set.perfexDesc')}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    settings?.perfexUrl && settings?.perfexToken
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {settings?.perfexUrl && settings?.perfexToken ? t('set.configured') : t('set.notConfigured')}
                  </span>
                  <button
                    onClick={() => setActiveModal('perfex')}
                    className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700"
                  >{t('set.configure')}</button>
                </div>
              </div>
            </div>
          </div>

          )}

          {/* Quem vê as integrações (apenas o dono da plataforma) */}
          {isPlatformOwner && (
            <div className="bg-white rounded-lg shadow p-6 mt-6">
              <h2 className="text-lg font-semibold mb-2 text-gray-900">👁️ {t('set.visibilityTitle')}</h2>
              <p className="text-gray-600 mb-4">{t('set.visibilityHelp')}</p>
              <div className="space-y-2">
                {([
                  ['showAiIntegrations', 'set.visibilityAi'],
                  ['showChatwootIntegration', 'set.visibilityChatwoot'],
                  ['showPerfexIntegration', 'set.visibilityPerfex'],
                ] as const).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={visibility[key]}
                      disabled={savingVisibility}
                      onChange={event => saveVisibility({ ...visibility, [key]: event.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-800">{t(label)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Cadastro self-service da plataforma */}
          {isPlatformOwner && (
            <div className="bg-white rounded-lg shadow p-6 mt-6">
              <h2 className="text-lg font-semibold mb-2 text-gray-900">🚪 {t('set.signupTitle')}</h2>
              <p className="text-gray-600 mb-6">
                {t('set.signupHelp')}
              </p>

              <form onSubmit={saveSignupSettings} className="space-y-4">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={signup.signupEnabled}
                    onChange={event => setSignup(prev => ({ ...prev, signupEnabled: event.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">{t('set.signupAllow')}</span>
                </label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="otpSessionName" className="block text-sm font-medium text-gray-700 mb-1">{t('set.signupConnection')}</label>
                    <select
                      id="otpSessionName"
                      value={signup.otpSessionName}
                      onChange={event => setSignup(prev => ({ ...prev, otpSessionName: event.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">{t('set.signupSelect')}</option>
                      {connections.map(connection => (
                        <option key={connection.name} value={connection.name}>
                          {connection.displayName || connection.name} {connection.status === 'WORKING' ? '' : `(${connection.status})`}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('set.signupConnectionHelp')}
                    </p>
                  </div>

                  <div>
                    <label htmlFor="signupTrialDays" className="block text-sm font-medium text-gray-700 mb-1">{t('set.trialDays')}</label>
                    <input
                      id="signupTrialDays"
                      type="number"
                      min={1}
                      max={365}
                      value={signup.signupTrialDays}
                      onChange={event => setSignup(prev => ({ ...prev, signupTrialDays: Number(event.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={savingSignup}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
                >
                  {savingSignup ? t('form.saving') : t('set.signupSave')}
                </button>
              </form>
            </div>
          )}

          {/* Horários de envio: a empresa escolhe quando falar com os próprios clientes */}
          <div className="bg-white rounded-lg shadow p-6 mt-6">
            <h2 className="text-lg font-semibold mb-2 text-gray-900">🕐 {t('win.title')}</h2>
            <p className="text-gray-600 mb-5">{t('win.help')}</p>

            <form onSubmit={saveWindows} className="space-y-3">
              <SendWindowsEditor windows={windows} onChange={setWindows} />

              <div className="flex flex-wrap items-center gap-3">
                <button type="submit" disabled={savingWindows} className="btn-primary text-sm disabled:opacity-50">
                  {savingWindows ? t('common.saving') : t('win.save')}
                </button>
                <span className="text-xs text-gray-500">{t('win.timezone', { zone: sendPolicy.sendTimezone })}</span>
              </div>
            </form>
          </div>

          {/* Política de envio responsável */}
          {canManageSendPolicy && (
            <div className="bg-white rounded-lg shadow p-6 mt-6">
              <h2 className="text-lg font-semibold mb-6 text-gray-900">
                🛡️ {t('set.policyTitle')}
              </h2>
              <p className="text-gray-600 mb-4">
                {t('set.policyHelp')}
              </p>
              {!canEditSendPolicy && (
                <p className="mb-6 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600">
                  🔒 {t('set.policyReadOnly')}
                </p>
              )}

              <form onSubmit={saveSendPolicy} className="space-y-4">
                <fieldset disabled={!canEditSendPolicy} className={canEditSendPolicy ? '' : 'opacity-70'}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="sendMinIntervalSeconds" className="block text-sm font-medium text-gray-700 mb-1">{t('set.minInterval')}</label>
                    <input
                      id="sendMinIntervalSeconds"
                      type="number"
                      min={5}
                      max={3600}
                      value={sendPolicy.sendMinIntervalSeconds}
                      onChange={(e) => setSendPolicy(prev => ({ ...prev, sendMinIntervalSeconds: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Minimum time between two messages from the same WhatsApp number (5–3600)
                    </p>
                  </div>

                  <div>
                    <label htmlFor="sendDailyLimitPerNumber" className="block text-sm font-medium text-gray-700 mb-1">{t('set.dailyLimit')}</label>
                    <input
                      id="sendDailyLimitPerNumber"
                      type="number"
                      min={1}
                      max={10000}
                      value={sendPolicy.sendDailyLimitPerNumber}
                      onChange={(e) => setSendPolicy(prev => ({ ...prev, sendDailyLimitPerNumber: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum new conversations started per WhatsApp number per day (1–10000)
                    </p>
                  </div>

                  <div>
                    <label htmlFor="sendWindowStart" className="block text-sm font-medium text-gray-700 mb-1">{t('set.windowStart')}</label>
                    <input
                      id="sendWindowStart"
                      type="time"
                      value={sendPolicy.sendWindowStart}
                      onChange={(e) => setSendPolicy(prev => ({ ...prev, sendWindowStart: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="sendWindowEnd" className="block text-sm font-medium text-gray-700 mb-1">{t('set.windowEnd')}</label>
                    <input
                      id="sendWindowEnd"
                      type="time"
                      value={sendPolicy.sendWindowEnd}
                      onChange={(e) => setSendPolicy(prev => ({ ...prev, sendWindowEnd: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('set.windowHelp')}</p>
                  </div>

                  <div>
                    <label htmlFor="sendTimezone" className="block text-sm font-medium text-gray-700 mb-1">{t('set.timezone')}</label>
                    <input
                      id="sendTimezone"
                      type="text"
                      value={sendPolicy.sendTimezone}
                      onChange={(e) => setSendPolicy(prev => ({ ...prev, sendTimezone: e.target.value }))}
                      placeholder="Asia/Dubai"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('set.timezoneHelp')}</p>
                  </div>

                  <div>
                    <label htmlFor="autoPauseFailureRate" className="block text-sm font-medium text-gray-700 mb-1">{t('set.autoPause')}</label>
                    <input
                      id="autoPauseFailureRate"
                      type="number"
                      min={5}
                      max={100}
                      value={sendPolicy.autoPauseFailureRate}
                      onChange={(e) => setSendPolicy(prev => ({ ...prev, autoPauseFailureRate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      The campaign is paused when the failure rate reaches this value (after 20 processed messages)
                    </p>
                  </div>
                </div>

                <label htmlFor="requireConsent" className="flex items-start gap-2 cursor-pointer">
                  <input
                    id="requireConsent"
                    type="checkbox"
                    checked={sendPolicy.requireConsent}
                    onChange={(e) => setSendPolicy(prev => ({ ...prev, requireConsent: e.target.checked }))}
                    className="h-4 w-4 mt-0.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-700">{t('set.requireConsent')}</span>
                    <span className="block text-xs text-gray-500">{t('set.requireConsentHelp')}</span>
                  </span>
                </label>

                <div>
                  <label htmlFor="optOutKeywords" className="block text-sm font-medium text-gray-700 mb-1">{t('set.optOutKeywords')}</label>
                  <textarea
                    id="optOutKeywords"
                    rows={2}
                    value={sendPolicy.optOutKeywords}
                    onChange={(e) => setSendPolicy(prev => ({ ...prev, optOutKeywords: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Comma-separated. A reply that is exactly one of these words unsubscribes the contact.
                  </p>
                </div>

                <div>
                  <label htmlFor="optOutConfirmation" className="block text-sm font-medium text-gray-700 mb-1">{t('set.optOutMessage')}</label>
                  <textarea
                    id="optOutConfirmation"
                    rows={3}
                    value={sendPolicy.optOutConfirmation}
                    onChange={(e) => setSendPolicy(prev => ({ ...prev, optOutConfirmation: e.target.value }))}
                    placeholder={t('set.optOutPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                </fieldset>

                {canEditSendPolicy && (
                  <div className="flex justify-end pt-2">
                    <button
                      type="submit"
                      disabled={savingSendPolicy}
                      className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900 disabled:opacity-50"
                    >
                      {savingSendPolicy ? t('form.saving') : t('set.savePolicy')}
                    </button>
                  </div>
                )}
              </form>
            </div>
          )}

        </div>
      </div>

      {/* Modais de Integração */}


      {/* Modal OpenAI */}
      {activeModal === 'openai' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">🤖 Configure OpenAI</h3>
              <button
                onClick={() => setActiveModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label htmlFor="openaiApiKey" className="block text-sm font-medium text-gray-700 mb-1">{t('set.openaiKey')}</label>
                <input
                  id="openaiApiKey"
                  type="password"
                  {...register('openaiApiKey')}
                  placeholder="sk-..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.openaiApiKey && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.openaiApiKey.message}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">{t('set.openaiKeyHelp')}</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >{t('common.cancel')}</button>
                {settings?.openaiApiKey && (
                  <button
                    type="button"
                    onClick={() => removeIntegration('openai')}
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                  >{t('set.remove')}</button>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900 disabled:opacity-50"
                >
                  {isSubmitting ? t('form.saving') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Groq */}
      {activeModal === 'groq' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">⚡ Configure Groq</h3>
              <button
                onClick={() => setActiveModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label htmlFor="groqApiKey" className="block text-sm font-medium text-gray-700 mb-1">{t('set.groqKey')}</label>
                <input
                  id="groqApiKey"
                  type="password"
                  {...register('groqApiKey')}
                  placeholder="gsk_..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.groqApiKey && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.groqApiKey.message}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  API key for Groq AI integration in campaigns (fast and efficient models)
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >{t('common.cancel')}</button>
                {settings?.groqApiKey && (
                  <button
                    type="button"
                    onClick={() => removeIntegration('groq')}
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                  >{t('set.remove')}</button>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
                >
                  {isSubmitting ? t('form.saving') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Claude */}
      {activeModal === 'claude' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">✳ Configure Claude</h3>
              <button
                onClick={() => setActiveModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label htmlFor="anthropicApiKey" className="block text-sm font-medium text-gray-700 mb-1">{t('set.claudeKey')}</label>
                <input
                  id="anthropicApiKey"
                  type="password"
                  {...register('anthropicApiKey')}
                  placeholder="sk-ant-..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.anthropicApiKey && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.anthropicApiKey.message}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {t('set.claudeHelp')}
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >{t('common.cancel')}</button>
                {settings?.anthropicApiKey && (
                  <button
                    type="button"
                    onClick={() => removeIntegration('claude')}
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                  >{t('set.remove')}</button>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-amber-700 text-white rounded-md hover:bg-amber-800 disabled:opacity-50"
                >
                  {isSubmitting ? t('form.saving') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Chatwoot */}
      {activeModal === 'chatwoot' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">💬 Configure Chatwoot</h3>
              <button
                onClick={() => setActiveModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label htmlFor="chatwootUrl" className="block text-sm font-medium text-gray-700 mb-1">{t('set.chatwootUrl')}</label>
                <input
                  id="chatwootUrl"
                  type="url"
                  {...register('chatwootUrl')}
                  placeholder="https://app.chatwoot.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.chatwootUrl && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.chatwootUrl.message}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">{t('set.chatwootUrlHelp')}</p>
              </div>

              <div>
                <label htmlFor="chatwootAccountId" className="block text-sm font-medium text-gray-700 mb-1">{t('set.accountId')}</label>
                <input
                  id="chatwootAccountId"
                  type="text"
                  {...register('chatwootAccountId')}
                  placeholder="123456"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.chatwootAccountId && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.chatwootAccountId.message}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">{t('set.accountIdHelp')}</p>
              </div>

              <div>
                <label htmlFor="chatwootApiToken" className="block text-sm font-medium text-gray-700 mb-1">{t('set.apiToken')}</label>
                <input
                  id="chatwootApiToken"
                  type="password"
                  {...register('chatwootApiToken')}
                  placeholder="••••••••••••••••"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.chatwootApiToken && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.chatwootApiToken.message}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  API token from your profile (found under Settings &gt; Profile)
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >{t('common.cancel')}</button>
                {settings?.chatwootUrl && settings?.chatwootAccountId && settings?.chatwootApiToken && (
                  <button
                    type="button"
                    onClick={() => removeIntegration('chatwoot')}
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                  >{t('set.remove')}</button>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSubmitting ? t('form.saving') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Perfex CRM */}
      <PerfexSyncModal
        isOpen={activeModal === 'perfex'}
        onClose={() => setActiveModal(null)}
        perfexUrl={settings?.perfexUrl || ''}
        perfexToken={settings?.perfexToken || ''}
        onSave={async (url, token) => {
          try {
            let requestData: any = {
              perfexUrl: url,
              perfexToken: token
            };

            if (user?.role === 'SUPERADMIN') {
              const selectedTenantId = localStorage.getItem('superadmin_selected_tenant');
              if (selectedTenantId) {
                requestData.tenantId = selectedTenantId;
              }
            }

            const response = await authenticatedFetch('/api/settings', {
              method: 'PUT',
              body: JSON.stringify(requestData),
            });

            if (response.ok) {
              toast.success(t('set.okPerfex'));
              await loadSettings();
            } else {
              const errorData = await response.json();
              toast.error(errorData.error || t('set.errPerfex'));
            }
          } catch (error) {
            console.error('Erro ao configurar Perfex CRM:', error);
            toast.error(t('set.errPerfex'));
          }
        }}
      />

    </>
  );
}